import {
  FlexBox,
  LadsControlField,
  Loader,
  MethodCall,
  MultiStateDiscreteControl,
  space,
  TwoStateDiscreteControl,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

import type {
  MethodInfo,
  SerializedVariant,
} from "../../server/lads-api/clients/lcc-openapi";
import { receiveDeviceMethods } from "../api/lads";
import { useAccessToken } from "../auth-provider";
import {
  coerceArgValue,
  type ControlFunction,
  isAnalogControl,
  isDiscreteControl,
  isTwoStateControl,
  useControl,
} from "../hooks/use-control";
import {
  ladsWebSocketService,
  type UAFunctionValueMessage,
} from "../util/websocket-lads";
import type { Device } from "../view-model";

export type LADSDeviceControlProps = {
  device: Device;
};

// The control fetch + the write/call operations live in the useControl hook —
// the single path for all device writes. This file keeps only the view wiring:
// live WS overlay, disabled reasons, and rendering. Discrete control rendering
// and the analog/method inputs come from the component library (lads-controls/*).

/** Why an operable-but-locked control can't be written, or null when it can.
 *  Disabled functions (isEnabled === false) are filtered out before render, so
 *  they never reach here. Ordered by precedence: device-level state first,
 *  then write permission. */
function getDisabledReason(
  controlFunction: ControlFunction,
  deviceStatus: string | undefined,
  t: (key: string, fallback: string) => string
): string | null {
  if (deviceStatus === "offline")
    return t("detail.control.disabled.offline", "Gerät ist offline");
  if (deviceStatus === "error")
    return t("detail.control.disabled.error", "Gerät im Fehlerzustand");
  if (!controlFunction.isUserWritable)
    return t(
      "detail.control.disabled.notWritable",
      "Kein Schreibrecht – userAccessLevel enthält kein currentWrite"
    );
  return null;
}

/** Method label with its owning function as context, so the per-function
 *  state-machine methods (Start/Stop on every controller, Open/Close on a cover)
 *  are distinguishable instead of looking like duplicates. The function is the
 *  browsePath segment after "FunctionSet"; falls back to the bare method name. */
function methodLabel(method: MethodInfo): string {
  const name = method.displayName || method.name;
  const parts = method.browsePath.split("/");
  const fnIdx = parts.indexOf("FunctionSet");
  const context = fnIdx >= 0 ? parts[fnIdx + 1] : undefined;
  return context && context !== name ? `${context} · ${name}` : name;
}

/** One callable method: an input field per argument, a call button, and the
 *  device's output / error from the last call. The call goes through the
 *  useControl hook (single write/call path). */
const MethodCallRow: FC<{
  method: MethodInfo;
  disabled: boolean;
  call: ReturnType<typeof useControl>["call"];
}> = ({ call, disabled, method }) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCalling, setIsCalling] = useState(false);

  // The library MethodCall owns the field inputs and hands us the raw string
  // values; we coerce them to the argument DataTypes and perform the call.
  const handleCall = async (values: Record<string, string>) => {
    setIsCalling(true);
    setError(null);
    setResult(null);
    try {
      const inputArguments: SerializedVariant[] = (
        method.inputArguments ?? []
      ).map((arg) => ({
        dataType: arg.dataType,
        value: coerceArgValue(arg.dataType, values[arg.name] ?? ""),
      }));
      const res = await call(method.browsePath, inputArguments);
      const outs = res.outputArguments ?? [];
      setResult(
        outs.length > 0
          ? outs
              .map(
                (o, i) =>
                  `${method.outputArguments?.[i]?.name ?? `out${i}`} = ${String(
                    o.value
                  )}`
              )
              .join(", ")
          : t("detail.method.ok", "OK")
      );
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : String(error_));
    } finally {
      setIsCalling(false);
    }
  };

  return (
    <MethodCall
      callLabel={t("detail.method.call", "Aufrufen") ?? undefined}
      disabled={disabled}
      error={error}
      inputArguments={method.inputArguments}
      isCalling={isCalling}
      name={methodLabel(method)}
      onCall={handleCall}
      result={result}
    />
  );
};

export const LADSDeviceControl: FC<LADSDeviceControlProps> = ({ device }) => {
  const { t } = useTranslation();
  const accessToken = useAccessToken();

  // Single control interface: the device's controls + the write/call ops.
  const { call, controls, error, isLoading, write } = useControl(device.id);

  // Reconstructed callable methods of the device (separate from control
  // functions — these are OPC UA methods, surfaced as "Aufrufen" buttons).
  const { data: deviceMethods } = useSWR(
    device.id ? ["deviceMethods", device.id] : null,
    () => receiveDeviceMethods(device.id, accessToken ?? undefined),
    { revalidateOnFocus: false, refreshInterval: 30_000 }
  );

  // Live values via WebSocket. The SWR fetch above only refreshes every 30s,
  // so a TargetValue write would otherwise take up to 30s to reflect in the
  // button highlight. The backend already emits the device-confirmed value as
  // a UAFunctionValue ~1-2s after the write — consume it here for instant,
  // device-confirmed updates. Keyed by functionPath.
  const roomId = device.location?.room;
  const [liveValues, setLiveValues] = useState<
    Map<
      string,
      {
        currentValue?: string | number;
        targetValue?: string | number;
        isEnabled?: boolean;
      }
    >
  >(new Map());

  // Join the device's room so the gateway forwards its value changes.
  // subscribe()/unsubscribe() are reference-counted, so this is safe alongside
  // other subscribers of the same room.
  useEffect(() => {
    if (!roomId) return;
    ladsWebSocketService.subscribe(roomId);
    return () => ladsWebSocketService.unsubscribe(roomId);
  }, [roomId]);

  useEffect(() => {
    const unsubscribe = ladsWebSocketService.onValue(
      (message: UAFunctionValueMessage) => {
        if (message.deviceId !== device.id) return;
        const vt = message.variableType.toLowerCase();
        if (!["targetvalue", "currentvalue", "isenabled"].includes(vt)) return;
        // Reconstruct the control's functionPath from the wire message so it
        // matches ControlFunction.functionPath exactly (deviceId + unitId + fn).
        const functionPath = `DeviceSet/${message.deviceId}/FunctionalUnitSet/${message.unitId}/FunctionSet/${message.functionName}`;
        const value = message.data?.value;
        setLiveValues((prev) => {
          const next = new Map(prev);
          const entry = { ...next.get(functionPath) };
          switch (vt) {
            case "targetvalue": {
              entry.targetValue = value as string | number;
              break;
            }
            case "currentvalue": {
              entry.currentValue = value as string | number;
              break;
            }
            case "isenabled": {
              {
                entry.isEnabled = !!value;
                // No default
              }
              break;
            }
          }
          next.set(functionPath, entry);
          return next;
        });
      }
    );
    return () => unsubscribe();
  }, [device.id]);

  // Overlay live WS values on top of the polled snapshot, then drop disabled
  // functions (isEnabled === false) entirely — matching the "Aktuelle Werte"
  // page, which also hides them rather than greying them out.
  const controlFunctionsLive = useMemo(
    () =>
      controls
        .map((cf) => {
          const live = liveValues.get(cf.functionPath);
          if (!live) return cf;
          return {
            ...cf,
            ...(live.currentValue !== undefined && {
              currentValue: live.currentValue,
            }),
            ...(live.targetValue !== undefined && {
              targetValue: live.targetValue,
            }),
            ...(live.isEnabled !== undefined && { isEnabled: live.isEnabled }),
          };
        })
        .filter((cf) => cf.isEnabled !== false),
    [controls, liveValues]
  );

  const handleControlClick = async (
    controlFunction: ControlFunction,
    value: string
  ) => {
    if (!controlFunction.isUserWritable) return;

    // Ignore empty / non-numeric analog input (e.g. a blur without a change) so
    // we never write a stray 0 to the device.
    if (
      isAnalogControl(controlFunction) &&
      (value === "" || Number.isNaN(Number(value)))
    ) {
      return;
    }

    // Analog setpoints are numeric; discrete/enum selections stay strings.
    const payload = isAnalogControl(controlFunction) ? Number(value) : value;

    try {
      // The new value flows back on its own via the WS value subscription once
      // the device applies it — no manual refetch needed.
      await write(controlFunction.functionPath, payload);
    } catch {
      // Failed to set target value — UI keeps the device-reported value.
    }
  };

  if (isLoading) {
    return (
      <FlexBox alignItems="center" gap={space(2)}>
        <Loader size="24px" />
        <Typography>{t("detail.control.loading")}</Typography>
      </FlexBox>
    );
  }

  if (error) {
    return (
      <Typography color="grey400">
        {t("detail.control.error") || "Failed to load control functions"}
      </Typography>
    );
  }

  const methods = (deviceMethods ?? []).filter(
    // Unit-level actions (on FunctionalUnitState, e.g. a whole-unit "Stop")
    // brauchen wir aktuell nicht. Zum Wiederanzeigen diese filter()-Zeile entfernen:
    (m) => !m.browsePath.includes("/FunctionalUnitState/")
  );

  if (controlFunctionsLive.length === 0 && methods.length === 0) {
    return (
      <Typography color="grey400">
        {t("detail.control.noControls") || "No control functions available"}
      </Typography>
    );
  }

  const methodsDisabled =
    device.state?.status === "offline" || device.state?.status === "error";

  return (
    <FlexBox flexDirection="column" gap={space(6)}>
      {controlFunctionsLive.map((controlFunction) => {
        const disabledReason = getDisabledReason(
          controlFunction,
          device.state?.status,
          t
        );
        const isDisabled = disabledReason !== null;

        const label =
          controlFunction.unitDisplayName === controlFunction.displayName
            ? controlFunction.displayName
            : `${controlFunction.displayName} (${controlFunction.unitDisplayName})`;
        // Discrete options: the commanded target is the selection. currentValue
        // is the live physical position (sweeps through intermediate stops while
        // moving), so we highlight targetValue, not currentValue.
        const options = (controlFunction.enumStrings ?? []).map((value) => ({
          value,
          label: t(`control.value.${value}`, value),
        }));
        const selected =
          controlFunction.targetValue == null
            ? undefined
            : String(controlFunction.targetValue);
        const onSelect = (value: string) =>
          handleControlClick(controlFunction, value);

        if (!isDiscreteControl(controlFunction)) return null;

        return (
          <LadsControlField
            disabledReason={disabledReason}
            key={controlFunction.functionPath}
            label={label}
          >
            {isTwoStateControl(controlFunction) ? (
              <TwoStateDiscreteControl
                disabled={isDisabled}
                onSelect={onSelect}
                options={options}
                selected={selected}
              />
            ) : (
              <MultiStateDiscreteControl
                disabled={isDisabled}
                onSelect={onSelect}
                options={options}
                selected={selected}
              />
            )}
          </LadsControlField>
        );
      })}

      {methods.length > 0 && (
        <FlexBox flexDirection="column" gap={space(4)}>
          <Typography block weight="semiBold">
            {t("detail.method.section", "Methoden")}
          </Typography>
          {methods.map((method) => (
            <MethodCallRow
              call={call}
              disabled={methodsDisabled}
              key={method.browsePath}
              method={method}
            />
          ))}
        </FlexBox>
      )}
    </FlexBox>
  );
};
